// Card "Notificações recentes" pra renderizar no topo da home. Mostra até
// 3 não-lidas; clique no item abre o modal direto, clique em "Ver todas"
// vai pra página completa. Some quando não há nada.

import { useState } from "preact/hooks";
import { Bell, ChevronRight } from "lucide-react";
import {
  hideNotif,
  NotificationModal,
  notifsState,
  recent3Unread,
  setRead
} from "./NotificationsView";
import type { NotificationRow } from "./api";

interface Props {
  onOpenAll: () => void;
}

export function NotificationsHomeCard({ onOpenAll }: Props) {
  const recent = recent3Unread.value;
  const totalUnread = notifsState.value.unreadCount;
  const isEmpty = recent.length === 0;
  const [openItem, setOpenItem] = useState<NotificationRow | null>(null);

  function openModal(n: NotificationRow) {
    setOpenItem(n);
    if (!n.readAt) void setRead(n.id, true);
  }

  return (
    <section class="overflow-hidden rounded-md border border-primary/40 bg-primary/5 p-3 sm:p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex min-w-0 items-center gap-2 text-sm font-bold text-primary">
          <Bell className="size-4 shrink-0" />
          <span class="truncate">Notificações recentes</span>
          {totalUnread > 0 && (
            <span class="inline-flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-primary/15 px-1.5 text-[11px] font-bold tabular-nums text-primary ring-1 ring-primary/25">
              {totalUnread}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onOpenAll}
          class="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          Ver todas <ChevronRight className="size-3.5" />
        </button>
      </header>
      {isEmpty ? (
        <p class="rounded-md border border-dashed border-primary/20 bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          Sem notificações novas por aqui.
        </p>
      ) : (
        <ul class="space-y-1.5">
          {recent.map((n) => (
            <li
              key={n.id}
              onClick={() => openModal(n)}
              class="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background/60 px-3 py-2 hover:bg-accent/50"
            >
              <span class="mt-0.5 inline-block size-2 shrink-0 rounded-full bg-primary" />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-semibold">{n.title}</div>
                {n.body && (
                  <div class="mt-0.5 truncate text-[11px] text-muted-foreground">
                    {n.body}
                  </div>
                )}
              </div>
              <span class="shrink-0 text-[10px] text-muted-foreground">
                {formatShort(n.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {openItem && (
        <NotificationModal
          n={openItem}
          onClose={() => setOpenItem(null)}
          onHide={() => {
            void hideNotif(openItem.id);
            setOpenItem(null);
          }}
          onUnread={() => {
            void setRead(openItem.id, false);
            setOpenItem(null);
          }}
        />
      )}
    </section>
  );
}

function formatShort(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}
